import { AxiosRequestConfig } from 'axios';

import ENVIRONMENT from '@/environments/environment';
import { ClientTaxonomyProps } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { Analytics } from '@/services/analytics';

import { setClarityCustomTags } from '../../services/clarity';
import { resolveMerchantLevel } from '../../utils/analytics';
import Api from '../../utils/apiHelper';
import MethodDashboard from '../../utils/dashboardMethod';
import {
  allResponse,
  CancelReason,
  CommercialOffer,
  CommercialOffersApiResponse,
  Dashboards,
  Merchant,
  OrderSortType,
} from './operationsInterface';

const OperationsAPI = {
  getMerchants: async (): Promise<Merchant> => {
    const URL = ENVIRONMENT.URL_API_MERCHANT
      + ENVIRONMENT.URL_API_MERCHANT_APIS.MERCHANTS
      + ENVIRONMENT.URL_API_MERCHANT_APIS.MERCHANTS_EXPAND;
    const response = await Api.GET(URL);
    const merchant = response.data as Merchant;
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.MERC_LEVEL]: resolveMerchantLevel(merchant.level),
      [ClientTaxonomyProps.MERC_SECTOR]: merchant.sector,
      [ClientTaxonomyProps.MERC_SALES_TYPE]: merchant.sales_type,
      [ClientTaxonomyProps.MERC_COMMERCIAL_AGENT]:
        merchant.commercial_agent.email.split('@')[0],
      [ClientTaxonomyProps.MERC_COMMERCIAL_NAME]: merchant.commercial_name,
    });
    setClarityCustomTags({
      [ClientTaxonomyProps.MERC_SECTOR]: merchant.sector,
      [ClientTaxonomyProps.MERC_SALES_TYPE]: merchant.sales_type,
      [ClientTaxonomyProps.MERC_COMMERCIAL_AGENT]:
        merchant.commercial_agent.email.split('@')[0],
      [ClientTaxonomyProps.MERC_COMMERCIAL_NAME]: merchant.commercial_name,
    });
    return merchant;
  },
  getDashboards: async (
    multipleFilters?: any,
    orderBy?: OrderSortType,
    config?: AxiosRequestConfig,
  ): Promise<allResponse<Dashboards>> => {
    const URL = ENVIRONMENT.URL_API_DASHBOARD
      + ENVIRONMENT.URL_API_DASHBOARD_APIS.DASHBOARD;
    const newURL = MethodDashboard(URL, multipleFilters, orderBy);
    const response = await Api.GET(newURL, config);
    return response;
  },
  getDashboard: async (
    creditProposalId: string,
  ): Promise<allResponse<Dashboards>> => {
    const URL = `${ENVIRONMENT.URL_API_DASHBOARD
      + ENVIRONMENT.URL_API_DASHBOARD_APIS.DASHBOARD_PROPOSAL
      }?credit_proposal_id=${creditProposalId}`;
    const response = await Api.GET(URL);
    return response;
  },
  getCommercialOffers: async (/* isPreferred: boolean */): Promise<
    allResponse<CommercialOffersApiResponse>
  > => {
    //  const flowString = `&flows=ECO&flows=CEX&flows=CBR&flows=SUS&preferred=true`; 
    const flowString = `&flows=ECO&flows=CEX&flows=CBR&flows=SUS`;
    const URL = `${ENVIRONMENT.URL_API_MERCHANT
      + ENVIRONMENT.URL_COMMERCIAL_OFFER.COMMERCIAL_OFFERS
      }?currency=EUR${flowString}`;
    const response = await Api.GET(URL);
    let existingECO = false;
    let existingCEX = false;
    let existingCBR = false;
    let existingSUS = false;
    response.data.flow_commercial_offers.forEach((offer: any) => {
      switch (offer.flow) {
        case 'ECO':
          existingECO = true;
          break;
        case 'CEX':
          existingCEX = true;
          break;
        case 'CBR':
          existingCBR = true;
          break;
        case 'SUS':
          existingSUS = true;
          break;
        default:
          break;
      }
    });
    const totalCommercialOffers: any[] = [];
    response.data.flow_commercial_offers.forEach((offer: any) => {
      offer.commercial_offers.forEach((commercialOffer: any) => {
        totalCommercialOffers.push(commercialOffer);
      });
    });
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.MERC_HAS_ECO_OFFERS]: existingECO.toString(),
      [ClientTaxonomyProps.MERC_HAS_CEX_OFFERS]: existingCEX.toString(),
      [ClientTaxonomyProps.MERC_HAS_CBR_OFFERS]: existingCBR.toString(),
      [ClientTaxonomyProps.MERC_HAS_SUS_OFFERS]: existingSUS.toString(),
      [ClientTaxonomyProps.OFFERS_MAX_AMOUNT]: Math.max(
        ...totalCommercialOffers.map(
          (offer: CommercialOffer) => offer.max_amount,
        ),
      ).toString(),
      [ClientTaxonomyProps.OFFERS_MIN_AMOUNT]: Math.min(
        ...totalCommercialOffers.map(
          (offer: CommercialOffer) => offer.min_amount,
        ),
      ).toString(),
      [ClientTaxonomyProps.OFFERS_MIN_TERM]: Math.min(
        ...totalCommercialOffers.map((offer: CommercialOffer) => offer.term),
      ).toString(),
      [ClientTaxonomyProps.OFFERS_MAX_TERM]: Math.max(
        ...totalCommercialOffers.map((offer: CommercialOffer) => offer.term),
      ).toString(),
      [ClientTaxonomyProps.OFFERS_COUNT]:
        totalCommercialOffers.length.toString(),
    });
    return response;
  },
  deleteOperation: async (creditProposalId: string, reason: CancelReason): Promise<any> => {
    const URL = `${ENVIRONMENT.URL_PROPOSAL.API_CREDIT_PROPOSAL + ENVIRONMENT.URL_PROPOSAL.PROPOSAL}/${creditProposalId}?reason=${reason}`;
    const response = await Api.DELETE(URL);
    return response;
  },
};

export const {
  getMerchants,
  getDashboards,
  getDashboard,
  getCommercialOffers,
  deleteOperation,
} = OperationsAPI;
